// ============================================================
// THE COMPANY — BriefingBuilder Component
// Generates briefing memos from decisions to a target role.
// ============================================================

import { useMemo, useState, useCallback } from "react";
import Modal from "../shared/Modal";
import CopyButton from "../shared/CopyButton";
import { useCompanyStore } from "../../store/useCompanyStore";
import { generateBriefingMemo } from "../../lib/promptTemplates";
import type { Decision } from "../../lib/types";

interface BriefingBuilderProps {
  roleId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function BriefingBuilder({
  roleId,
  isOpen,
  onClose,
}: BriefingBuilderProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const roles = useCompanyStore((s) => s.roles);
  const decisions = useCompanyStore((s) => s.decisions);
  const informRoles = useCompanyStore((s) => s.informRoles);

  const role = useMemo(
    () => roles.find((r) => r.id === roleId),
    [roles, roleId],
  );

  // All decisions NOT authored by this role (you don't brief yourself)
  const relevantDecisions = useMemo(
    () => decisions.filter((d) => d.roleId !== roleId),
    [decisions, roleId],
  );

  const toggleDecision = useCallback((decisionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(decisionId)) {
        next.delete(decisionId);
      } else {
        next.add(decisionId);
      }
      return next;
    });
  }, []);

  // Generate combined briefing text for all selected decisions
  const combinedBriefing = useMemo(() => {
    if (!role) return "";
    const memos: string[] = [];
    for (const decision of relevantDecisions) {
      if (!selectedIds.has(decision.id)) continue;
      const sourceRole = roles.find((r) => r.id === decision.roleId);
      if (!sourceRole) continue;
      memos.push(generateBriefingMemo(sourceRole, role, decision));
    }
    return memos.join("\n\n" + "=".repeat(40) + "\n\n");
  }, [role, roles, relevantDecisions, selectedIds]);

  // On copy, also mark roles as informed
  const handleCopyAndInform = useCallback(async () => {
    if (selectedIds.size === 0) return;
    for (const decisionId of selectedIds) {
      try {
        await informRoles(decisionId, [roleId]);
      } catch {
        // Swallow — the copy still succeeded
      }
    }
  }, [selectedIds, informRoles, roleId]);

  if (!role) return null;

  const hasSelection = selectedIds.size > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Brief — ${role.name}`}
      width="640px"
    >
      {/* Decision selector */}
      <div className="mb-4">
        <p
          className="text-xs font-semibold uppercase tracking-wider mb-2"
          style={{ color: "var(--text-muted)" }}
        >
          Select decisions to brief
        </p>

        {relevantDecisions.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            No decisions available to brief about.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-[200px] overflow-auto">
            {relevantDecisions.map((decision) => (
              <DecisionRow
                key={decision.id}
                decision={decision}
                roleId={roleId}
                roles={roles}
                isSelected={selectedIds.has(decision.id)}
                onToggle={toggleDecision}
              />
            ))}
          </div>
        )}
      </div>

      {/* Preview */}
      {hasSelection && (
        <>
          <p
            className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: "var(--text-muted)" }}
          >
            Briefing Preview
          </p>
          <div
            className="rounded-lg overflow-auto max-h-[40vh] p-4 text-sm font-mono leading-relaxed whitespace-pre-wrap"
            style={{
              backgroundColor: "var(--bg-base)",
              border: "1px solid var(--border-subtle)",
              color: "var(--text-secondary)",
            }}
          >
            {combinedBriefing}
          </div>
        </>
      )}

      {/* Copy + inform button */}
      <div className="flex justify-end mt-4">
        <div onClick={handleCopyAndInform}>
          <CopyButton
            text={combinedBriefing}
            label={`Copy Briefing${hasSelection ? ` (${selectedIds.size})` : ""}`}
          />
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Decision row with checkbox
// ---------------------------------------------------------------------------

function DecisionRow({
  decision,
  roleId,
  roles,
  isSelected,
  onToggle,
}: {
  decision: Decision;
  roleId: string;
  roles: { id: string; name: string }[];
  isSelected: boolean;
  onToggle: (id: string) => void;
}) {
  const isAlreadyInformed = decision.informedRoleIds.includes(roleId);
  const maker = roles.find((r) => r.id === decision.roleId);
  const dateStr = new Date(decision.createdAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

  const truncated =
    decision.content.length > 100
      ? decision.content.slice(0, 100) + "..."
      : decision.content;

  return (
    <button
      type="button"
      onClick={() => {
        if (!isAlreadyInformed) onToggle(decision.id);
      }}
      disabled={isAlreadyInformed}
      className={`
        w-full flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm
        transition-colors duration-150
        ${isAlreadyInformed ? "opacity-50 cursor-default" : "cursor-pointer hover:bg-[var(--bg-elevated)]"}
        ${isSelected && !isAlreadyInformed ? "ring-1 ring-[var(--accent)]" : ""}
      `}
      style={{
        backgroundColor:
          isSelected && !isAlreadyInformed
            ? "var(--bg-elevated)"
            : "transparent",
        border: "1px solid var(--border-subtle)",
      }}
    >
      {/* Checkbox / checkmark */}
      <span className="mt-0.5 shrink-0 w-4 h-4 flex items-center justify-center">
        {isAlreadyInformed ? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "var(--status-active)" }}
          >
            <polyline points="2 7 5.5 10.5 12 4" />
          </svg>
        ) : (
          <span
            className={`
              block w-3.5 h-3.5 rounded border transition-colors duration-150
              ${
                isSelected
                  ? "bg-[var(--accent)] border-[var(--accent)]"
                  : "border-[var(--border-default)]"
              }
            `}
          />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <p style={{ color: "var(--text-primary)" }}>{truncated}</p>
        <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
          {maker ? maker.name : "Unknown"} &middot; {dateStr}
          {isAlreadyInformed && (
            <span className="ml-1.5" style={{ color: "var(--status-active)" }}>
              &mdash; already informed
            </span>
          )}
        </p>
      </div>
    </button>
  );
}
